import { ElementType } from 'react';
import { Trans } from 'react-i18next';
import { TFunction } from 'i18next';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { ROUTES } from '@routes/routes';
import { NotificationPresenter } from '@notifications/interfaces';
import { FeedInteractionNotificationData } from './interfaces';

const isFeedInteractionPayload = (
  payload: unknown
): payload is FeedInteractionNotificationData => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  return 'actorName' in payload && typeof payload.actorName === 'string';
};

const feedInteractionLabel = (
  type: string,
  payload: FeedInteractionNotificationData,
  t: TFunction
): string => {
  const { actorName, resourceTitle } = payload;

  if (!resourceTitle) {
    return t(`notifications:types.${type}`, {
      actorName,
    });
  }

  return t(`notifications:types.${type}-with-title`, {
    actorName,
    resourceTitle,
  });
};

const feedInteractionMessage = (
  type: string,
  payload: FeedInteractionNotificationData
): JSX.Element => {
  const titleKey = payload.resourceTitle
    ? `notifications:menu.${type}-with-title`
    : `notifications:menu.${type}`;

  return (
    <Trans
      i18nKey={titleKey}
      values={{
        actorName: payload.actorName,
        resourceTitle: payload.resourceTitle,
      }}
      components={{
        bold: <strong />,
      }}
    />
  );
};

const feedInteractionPath = (
  payload: FeedInteractionNotificationData
): string | null => {
  const { resourceType, resourceId, resourceTitle } = payload;

  if (
    !resourceTitle ||
    !resourceType ||
    resourceId === null ||
    resourceId === undefined
  ) {
    return null;
  }

  if (resourceType === 'news-item') {
    return ROUTES.portal.news.details(String(resourceId));
  }

  if (resourceType === 'competition') {
    return ROUTES.portal.competitions.details(String(resourceId));
  }

  return null;
};

const createFeedInteractionPresenter = (
  type: string,
  icon: ElementType
): NotificationPresenter<FeedInteractionNotificationData> => ({
  type,
  icon,
  isPayload: isFeedInteractionPayload,
  label: (payload, t) => feedInteractionLabel(type, payload, t),
  message: (payload) => feedInteractionMessage(type, payload),
  path: feedInteractionPath,
});

export const feedInteractionPresenters: NotificationPresenter<FeedInteractionNotificationData>[] =
  [
    createFeedInteractionPresenter(
      'feed-message-commented',
      ChatBubbleOutlineIcon
    ),
    createFeedInteractionPresenter('feed-message-liked', FavoriteIcon),
    createFeedInteractionPresenter('feed-comment-liked', FavoriteBorderIcon),
  ];
